import { NgClass, NgFor, NgIf } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { LucideDynamicIcon } from "@lucide/angular";
import { Subscription } from "rxjs";
import { LanguageCode, LANGUAGE_OPTIONS } from "../core/language.model";
import { LanguageService } from "../core/language.service";

@Component({
  selector: "app-language-selector",
  standalone: true,
  imports: [LucideDynamicIcon, NgClass, NgFor, NgIf],
  template: `
    <div
      class="relative inline-block"
      [style.width]="compact ? '9rem' : '100%'"
    >
      <span class="sr-only">{{ language.t("language.label") }}</span>

      <button
        type="button"
        class="relative z-10 flex items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm transition hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        [ngClass]="className"
        [style.width]="'100%'"
        [style.min-width]="compact ? '9rem' : null"
        [style.height]="compact ? '2.25rem' : '2.5rem'"
        [attr.aria-label]="language.t('language.label')"
        aria-haspopup="listbox"
        [attr.aria-expanded]="isOpen"
        (click)="toggleOpen($event)"
      >
        <svg
          lucideIcon="globe"
          class="h-4 w-4 shrink-0 text-muted-foreground"
        ></svg>
        <span class="min-w-0 flex-1 truncate text-left">{{
          selectedLanguageLabel
        }}</span>
        <svg
          lucideIcon="chevron-down"
          class="h-4 w-4 shrink-0 text-muted-foreground transition-transform"
          [ngClass]="isOpen ? 'rotate-180' : ''"
        ></svg>
      </button>

      <div
        *ngIf="isOpen"
        class="absolute right-0 z-[80] mt-1 flex w-full min-w-36 flex-col overflow-hidden rounded-md border border-border bg-white shadow-lg"
        [style.height.px]="options.length * optionHeight"
        role="listbox"
        [attr.aria-label]="language.t('language.label')"
      >
        <button
          *ngFor="let option of options"
          type="button"
          role="option"
          class="flex w-full shrink-0 items-center justify-between gap-2 px-3 text-left text-sm transition"
          [style.height.px]="optionHeight"
          [style.backgroundColor]="
            option.code === currentLanguage ? '#7fcf9a' : '#ffffff'
          "
          [style.color]="
            option.code === currentLanguage ? '#ffffff' : '#173a63'
          "
          [style.fontWeight]="option.code === currentLanguage ? '700' : '500'"
          [attr.aria-selected]="option.code === currentLanguage"
          (click)="selectLanguage(option.code)"
        >
          <span class="truncate">{{ option.nativeLabel || option.label }}</span>
          <svg
            *ngIf="option.code === currentLanguage"
            lucideIcon="check"
            class="h-4 w-4 shrink-0"
          ></svg>
        </button>
      </div>
    </div>
  `,
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  readonly language = inject(LanguageService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() compact = false;
  @Input() className = "";

  readonly options = LANGUAGE_OPTIONS;
  readonly optionHeight = 36;
  currentLanguage: LanguageCode;
  isOpen = false;
  private subscription?: Subscription;

  constructor() {
    this.currentLanguage = this.language.language;
  }

  ngOnInit() {
    this.subscription = this.language.language$.subscribe((nextLanguage) => {
      this.currentLanguage = nextLanguage;
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  @HostListener("document:click", ["$event"])
  handleDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  get selectedLanguageLabel() {
    return (
      this.options.find((option) => option.code === this.currentLanguage)
        ?.nativeLabel || this.language.t(`language.${this.currentLanguage}`)
    );
  }

  toggleOpen(event: MouseEvent) {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  selectLanguage(nextLanguage: LanguageCode) {
    this.currentLanguage = nextLanguage;
    this.language.setLanguage(nextLanguage);
    this.isOpen = false;
  }
}
